import type { BaseItineraryBlockProps, ItineraryItemAction } from './BaseItineraryBlock.js';

import React from 'react';

import { useItineraryState, useTripsState } from '#states/hooks.js';
import { ItineraryDatesDialog } from '#ui/itinerary-dates-dialog.js';

import { BaseItineraryBlock } from './BaseItineraryBlock.js';
import { ItemNotesDialog } from './ItemNotesDialog.js';
import { bookingDetailActions, viewBookingDetailsAction } from './itinerary-booking-actions.js';
import { ItineraryDetailSummary } from './ItineraryDetailSummary.js';
import { ItineraryNotesSection } from './ItineraryNotesSection.js';

export function CustomBlock({ event, items, size = 'default', className }: BaseItineraryBlockProps) {
  const tripsState = useTripsState();
  const itineraryState = useItineraryState();
  const [isDatesDialogOpen, setIsDatesDialogOpen] = React.useState(false);
  const [isNotesDialogOpen, setIsNotesDialogOpen] = React.useState(false);

  if (event.type !== 'activity' && event.type !== 'custom') return null;

  const primaryItem = items[0]!;
  const time = event.formattedTime;
  const isDefault = size === 'default';

  const title = time && size === 'sm' ? `${event.title} at ${time}` : event.title;

  const actions: ItineraryItemAction[] = [
    ...viewBookingDetailsAction(primaryItem, (item) => itineraryState.openViewBookingDialog(item)),
    ...bookingDetailActions(primaryItem, itineraryState, { add: isDefault ? 'secondary' : 'tertiary' }),
    {
      type: isDefault ? 'secondary' : 'tertiary',
      label: 'Change time',
      icon: 'schedule',
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsDatesDialogOpen(true);
      },
    },
  ];

  if (isDefault) {
    actions.push({
      type: 'secondary',
      label: 'Remove',
      icon: 'delete',
      destructive: true,
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        itineraryState.openDeleteDialog(primaryItem);
      },
    });
  }

  const handleConfirmDates = async (dates: { startDateTime: string; endDateTime?: string }) => {
    await itineraryState.updateItemDates(primaryItem.id, {
      startDateTime: dates.startDateTime,
      endDateTime: dates.endDateTime,
    });
    setIsDatesDialogOpen(false);
  };

  return (
    <>
      <BaseItineraryBlock
        title={title}
        event={event}
        items={items}
        size={size}
        className={className}
        canDeleteItem={!isDefault}
        preHeaderContent={
          size === 'lg' &&
          time && (
            <>
              <div>·</div>
              <div className="text-xs uppercase tracking-wide">{time}</div>
            </>
          )
        }
        onTimeClick={isDefault ? () => setIsDatesDialogOpen(true) : undefined}
        actions={actions}
      >
        {isDefault ? (
          <>
            <ItineraryDetailSummary cost={event.formattedCost} costDetail={primaryItem.formattedCostDetail} />
            <ItineraryNotesSection
              notes={primaryItem.notes}
              advisorNote={primaryItem.booking?.advisorNote}
              onEditNote={() => setIsNotesDialogOpen(true)}
            />
          </>
        ) : (
          primaryItem.notes && <div className="max-w-prose text-pretty">{primaryItem.notes}</div>
        )}
      </BaseItineraryBlock>

      {isDatesDialogOpen && (
        <ItineraryDatesDialog
          open={isDatesDialogOpen}
          onOpenChange={setIsDatesDialogOpen}
          itemType="custom"
          referenceId={primaryItem.referenceId ?? undefined}
          title={event.title}
          initialStartDateTime={primaryItem.startDateTime}
          initialEndDateTime={primaryItem.endDateTime ?? undefined}
          tripStartDate={tripsState.current?.startDate ?? undefined}
          tripEndDate={tripsState.current?.endDate ?? undefined}
          onConfirm={handleConfirmDates}
          confirmLabel="Save"
        />
      )}

      {isNotesDialogOpen && (
        <ItemNotesDialog
          open={isNotesDialogOpen}
          onOpenChange={setIsNotesDialogOpen}
          initialNotes={primaryItem.notes}
          onConfirm={async (notes) => {
            await itineraryState.updateItemNotes(primaryItem.id, notes);
          }}
        />
      )}
    </>
  );
}
